import { useState } from 'react';

import {
  ChevronsUpDown,
  DatabaseZap,
  Link as LinkIcon,
  Loader2,
  PowerOff,
  Rocket,
  Settings,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { showPromiseToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { useGetCacheStatusQuery, useInvalidateCacheMutation } from '@/store/api/cacheApi';

interface CacheOption {
  label: string;
  icon: LucideIcon;
  color: string;
}

const CACHE_OPTIONS: Record<string, CacheOption> = {
  deployment_status_all: {
    label: 'Deployment Status',
    icon: Rocket,
    color: 'text-blue-600',
  },
  instance_config_all: {
    label: 'Instance Configs',
    icon: Settings,
    color: 'text-purple-600',
  },
  tunnel_sessions_active: {
    label: 'Active Tunnels',
    icon: LinkIcon,
    color: 'text-green-600',
  },
  shutdown_status_by_environment: {
    label: 'Auto-Shutdown Status',
    icon: PowerOff,
    color: 'text-orange-600',
  },
};

interface CacheRefreshComboboxProps {
  onRefreshComplete?: () => void;
}

const CacheRefreshCombobox = ({ onRefreshComplete }: CacheRefreshComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [selectedCache, setSelectedCache] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);

  const { data: cacheStatus, refetch: refetchCacheStatus } = useGetCacheStatusQuery();
  const [invalidateCache, { isLoading }] = useInvalidateCacheMutation();

  // Format relative time
  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never updated';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleString();
    }
  };

  // Get cache status for a specific key
  const getCacheInfo = (key: string) => {
    return cacheStatus?.caches.find((cache) => cache.key === key);
  };

  // Handle cache selection
  const handleSelect = (cacheKey: string) => {
    setSelectedCache(cacheKey);
    setOpen(false);
    setAlertOpen(true);
  };

  // Handle confirmation
  const handleConfirm = async () => {
    if (!selectedCache) return;

    setAlertOpen(false);

    await showPromiseToast(invalidateCache({ key: selectedCache }).unwrap(), {
      loading: `Refreshing ${CACHE_OPTIONS[selectedCache].label}...`,
      success: (data) => `${data.message}`,
      error: (error: any) => error?.data?.detail || error?.message || 'Failed to refresh cache',
    });

    // Refetch cache status to update last_updated times
    await refetchCacheStatus();

    // Call optional callback
    if (onRefreshComplete) {
      onRefreshComplete();
    }

    setSelectedCache(null);
  };

  const selectedCacheInfo = selectedCache ? getCacheInfo(selectedCache) : null;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className='w-[200px] justify-between'
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Refreshing...
              </>
            ) : (
              <>
                <span className='flex items-center gap-2'>
                  <DatabaseZap className='h-4 w-4 text-green-600' />
                  Refresh Cache
                </span>
                <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[300px] p-0' align='end' sideOffset={8}>
          <Command>
            <CommandInput placeholder='Search cache...' />
            <CommandList className='max-h-[400px]'>
              <CommandEmpty>No cache found.</CommandEmpty>
              <CommandGroup>
                {Object.entries(CACHE_OPTIONS).map(([key, option]) => {
                  const Icon = option.icon;
                  const info = getCacheInfo(key);

                  return (
                    <CommandItem key={key} value={key} onSelect={() => handleSelect(key)}>
                      <Icon className={cn('mr-2 h-4 w-4', option.color)} />
                      <div className='flex flex-col flex-1'>
                        <span className='font-medium'>{option.label}</span>
                        <span className='text-xs text-muted-foreground'>
                          {info?.description || 'Loading...'}
                        </span>
                        <span className='text-xs text-muted-foreground mt-1'>
                          Updated: {formatRelativeTime(info?.last_updated || null)}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Confirmation Alert Dialog */}
      <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Refresh {selectedCache ? CACHE_OPTIONS[selectedCache].label : 'Cache'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedCacheInfo && (
                <div className='space-y-2 pt-2'>
                  <p>This will invalidate the current cache and trigger a background refresh.</p>
                  <div className='rounded-md bg-muted p-3 space-y-1 text-sm'>
                    <p>
                      <span className='font-semibold'>Cache:</span> {selectedCacheInfo.description}
                    </p>
                    <p>
                      <span className='font-semibold'>Last Updated:</span>{' '}
                      {formatRelativeTime(selectedCacheInfo.last_updated)}
                    </p>
                  </div>
                  <p className='text-xs text-muted-foreground pt-2'>
                    The cache will be refreshed in the background. You may continue working while
                    the refresh completes.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>Refresh Cache</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CacheRefreshCombobox;
